import { Component, effect, ElementRef, input, output, signal, ViewChild } from '@angular/core'
import { NgClass } from '@angular/common'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { DragDropModule } from '@angular/cdk/drag-drop'
import { SocialMediaCard } from '@app/v2/event/types/event-social-media.type'
import { SafeUrlPipe } from '@app/modules/admin/pipes/safe-url.pipe'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { FormControl } from '@angular/forms'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { isKnownSocialMediaUrl } from '@app/v2/event/utils/is-known-social-media-url'

@Component({
  selector: 'app-social-media-card',
  templateUrl: './social-media-card.component.html',
  styleUrls: ['./social-media-card.component.scss'],
  standalone: true,
  imports: [NgxTippyModule, DragDropModule, SafeUrlPipe, NgClass, ModalComponent, BasicInputComponent]
})
export class SocialMediaCardComponent {
  protected readonly isKnownSocialMediaUrl = isKnownSocialMediaUrl
  public link = input.required<SocialMediaCard>()
  public enableDragging = input<boolean>(false)
  public renameControl = new FormControl('')
  public isEditing = signal(false)
  public imageLoadFailed = signal(false)
  public isActionButtonMouseDown = signal(false)
  public readonly socialMediaMobileSettingsModalId = crypto.randomUUID()
  public readonly socialMediaMobileRenameModalId = crypto.randomUUID()
  public readonly renameIcon = `<svg class="tw-text-gray-900" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <g clip-path="url(#clip0_39841_96266)">
            <path d="M12.4516 0.621206L11.1745 1.97018L14.1146 5.07562L15.391 3.72665C15.584 3.52276 15.7372 3.2807 15.8417 3.01428C15.9462 2.74786 16 2.4623 16 2.17393C16 1.88555 15.9462 1.6 15.8417 1.33358C15.7372 1.06716 15.584 0.825093 15.391 0.621206C14.9948 0.222442 14.4685 0 13.9213 0C13.374 0 12.8478 0.222442 12.4516 0.621206Z" fill="currentColor"/>
            <path d="M12.9389 6.31744L9.99872 3.212L6.06991 7.35815C5.95367 7.4807 5.87439 7.63686 5.84208 7.80693L5.40471 10.136C5.37781 10.2778 5.38448 10.4245 5.42413 10.5629C5.46379 10.7014 5.53521 10.8273 5.63204 10.9296C5.72888 11.0319 5.84812 11.1073 5.9792 11.1492C6.11027 11.1911 6.24911 11.1981 6.38338 11.1697L8.5885 10.7042C8.74923 10.6699 8.89677 10.5862 9.01257 10.4636L12.9389 6.31744Z" fill="currentColor"/><path d="M6.06991 7.35815C5.95367 7.4807 5.87439 7.63686 5.84208 7.80693L5.40471 10.136C5.37781 10.2778 5.38448 10.4245 5.42413 10.5629C5.46379 10.7014 5.53521 10.8273 5.63204 10.9296C5.72888 11.0319 5.84812 11.1073 5.9792 11.1492C6.11027 11.1911 6.24911 11.1981 6.38338 11.1697L8.5885 10.7042C8.74923 10.6699 8.89677 10.5862 9.01257 10.4636L12.9389 6.31744L9.99872 3.212L6.06991 7.35815Z" fill="currentColor"/><path d="M9.80581 11.4894C9.57348 11.7344 9.27787 11.9015 8.95602 11.9698L6.01586 12.5907C5.74746 12.6473 5.46997 12.6331 5.20802 12.5493C4.94607 12.4655 4.70776 12.3147 4.51422 12.1103C4.32068 11.9058 4.17791 11.6541 4.09856 11.3774C4.01921 11.1008 4.00574 10.8077 4.05935 10.5242L4.64722 7.41876C4.7119 7.07881 4.87012 6.76659 5.10205 6.5212L9.193 2.20028C9.17025 2.1184 9.15849 2.03355 9.15808 1.94822H1.80185C1.3241 1.94869 0.866052 2.14935 0.528235 2.50615C0.190419 2.86296 0.000440318 3.34676 0 3.85136V14.0969C0.000440318 14.6015 0.190419 15.0853 0.528235 15.4421C0.866052 15.7989 1.3241 15.9995 1.80185 16H11.5021C11.9798 15.9995 12.4379 15.7989 12.7757 15.4421C13.1135 15.0853 13.3035 14.6015 13.3039 14.0969V7.79464L9.80581 11.4894Z" fill="currentColor"/></g><defs><clipPath id="clip0_39841_96266"><rect width="16" height="16" fill="white"/></clipPath></defs>
          </svg>`
  public remove = output<void>()
  public updateTitle = output<string>()
  @ViewChild('linkName') public linkNameRef: ElementRef<HTMLElement>

  constructor(private readonly _modalService: ModalService) {
    effect(() => {
      this.link()
      this.imageLoadFailed.set(false)
    })
  }

  public get renamePlaceholder(): string {
    return `${this.link()?.title} link`
  }

  /**
   * Handle the click event for the link name
   */
  public onLinkNameClick(): void {
    if (this.isEditing()) return
    this.isEditing.set(true)
    // Focus the link name at the end of the text and select the text
    setTimeout(() => {
      const element = this.linkNameRef?.nativeElement
      if (!element) return
      element.focus()
      const range = document.createRange()
      range.selectNodeContents(element)
      range.collapse(false)
      const selection = document.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
    })
  }

  /**
   * Update the link name
   */
  public updateLinkName(newtitle?: string): void {
    if (!this.isEditing() || this.isActionButtonMouseDown()) return
    const title = newtitle || this.linkNameRef?.nativeElement?.textContent?.trim()
    if (!title) {
      this.onCancel()
      return
    }
    this.isEditing.set(false)
    this.updateTitle.emit(title)
  }

  /**
   * Cancel editing and revert the link name
   */
  public onCancel(): void {
    this.isEditing.set(false)
    if (this.linkNameRef?.nativeElement) {
      this.linkNameRef.nativeElement.textContent = this.link().title
    }
  }

  /**
   * Remove the social media link
   */
  public onRemove(): void {
    this.remove.emit()
  }

  /**
   * Handle the image error event
   */
  public onImageError(): void {
    this.imageLoadFailed.set(true)
  }

  /**
   * Handle the enter key event
   * @param event {KeyboardEvent} - The keyboard event
   */
  public onEnter(event: KeyboardEvent): void {
    event.preventDefault()
    this.updateLinkName()
  }

  /**
   * Handle the action button mouse down event
   * @param event {MouseEvent} - The mouse event
   */
  public onActionButtonMouseDown(event: MouseEvent): void {
    event.stopPropagation()
    this.isActionButtonMouseDown.set(true)
  }

  /**
   * Handle the action button mouse up event
   * @param event {MouseEvent} - The mouse event
   */
  public onActionButtonMouseUp(): void {
    this.isActionButtonMouseDown.set(false)
  }

  public isModalOpened(modalId: string): boolean {
    return this._modalService.isModalOpen(modalId)
  }

  public toggleModal(modalId: string): void {
    this._modalService.toggleModal(modalId)
  }

  public onOpenRenameModal(): void {
    this.renameControl.setValue(this.link()?.title)
    this.toggleModal(this.socialMediaMobileSettingsModalId)
    this.toggleModal(this.socialMediaMobileRenameModalId)
  }

  public onRename(): void {
    this.isEditing.set(true)
    this.updateLinkName(this.renameControl.value)
    this.toggleModal(this.socialMediaMobileRenameModalId)
  }

  public onKeydownRename(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onRename()
    }
  }

  public onRemoveClicked(): void {
    this.onRemove()
    this.toggleModal(this.socialMediaMobileSettingsModalId)
  }
}
